/**
 * <plp-video> — video driven by the PLP control pill (sound / play / pause).
 * Native controls are off, so button state is mirrored from the video, which
 * can also change on its own (ended, tab hidden). Autoplay requires muted, so
 * unmuting is always an explicit user action.
 */
class PlpVideo extends HTMLElement {
  connectedCallback() {
    if (this.initialised) return;
    this.initialised = true;

    this.video = this.querySelector('video');
    if (!this.video) return;

    this.soundBtn = this.querySelector('[data-plp-video-sound]');
    this.playBtn = this.querySelector('[data-plp-video-play]');
    this.pauseBtn = this.querySelector('[data-plp-video-pause]');

    if (this.soundBtn) this.soundBtn.addEventListener('click', () => this.toggleSound());
    if (this.playBtn) this.playBtn.addEventListener('click', () => this.play());
    if (this.pauseBtn) this.pauseBtn.addEventListener('click', () => this.video.pause());

    this.sync = this.sync.bind(this);
    ['play', 'pause', 'ended', 'volumechange'].forEach((evt) => this.video.addEventListener(evt, this.sync));

    this.observeViewport();
    this.sync();
  }

  disconnectedCallback() {
    if (this.viewportObserver) this.viewportObserver.disconnect();
  }

  play() {
    // Rejection is normal here (autoplay policy, or a pause landing first).
    const attempt = this.video.play();
    if (attempt && typeof attempt.catch === 'function') attempt.catch(() => {});
  }

  toggleSound() {
    this.video.muted = !this.video.muted;
    if (!this.video.muted && this.video.paused) this.play();
    // volumechange is async; sync now so the icon swaps on the click's tick.
    this.sync();
  }

  /** Pauses off-screen; resumes only if we were the one who paused. */
  observeViewport() {
    if (!('IntersectionObserver' in window)) return;

    this.viewportObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          if (this.pausedOffscreen) {
            this.pausedOffscreen = false;
            this.play();
          }
        } else if (!this.video.paused) {
          this.pausedOffscreen = true;
          this.video.pause();
        }
      });
    }, { threshold: 0 });

    this.viewportObserver.observe(this);
  }

  sync() {
    const muted = this.video.muted;
    const playing = !this.video.paused && !this.video.ended;

    this.classList.toggle('is-playing', playing);
    this.classList.toggle('is-muted', muted);

    if (this.soundBtn) {
      this.soundBtn.setAttribute('aria-pressed', muted ? 'false' : 'true');
      this.soundBtn.setAttribute('aria-label', this.soundBtn.dataset[muted ? 'labelUnmute' : 'labelMute'] || '');
    }
    if (this.playBtn) this.playBtn.disabled = playing;
    if (this.pauseBtn) this.pauseBtn.disabled = !playing;
  }
}

customElements.define('plp-video', PlpVideo);
